import { foldArabicDigits } from "@a2z/text";
import type { DispatchStatus } from "@/lib/api/dispatchTypes";

/**
 * Sharing a delivery's tracking link from the till (driver backend B4 · P16,
 * plan D-26), the pure half.
 *
 * The backend never sends the link: the cashier shares it from the
 * restaurant's own WhatsApp through the `wa.me` share intent addressed to the
 * customer's phone on the bill, or copies it. Then the till records the share
 * (T10) so the driver's app reads «shared with the customer».
 */

/** From the driver's accept until the bag reaches the door (plan: "ACCEPTED onward"). */
const SHAREABLE: ReadonlySet<DispatchStatus> = new Set(["ACCEPTED", "PICKED_UP", "ARRIVED"]);

export function isTrackingShareable(status: DispatchStatus | null | undefined): boolean {
  return status != null && SHAREABLE.has(status);
}

/**
 * The customer's phone as `wa.me` wants it (country code + number, digits
 * only), or null when the bill's phone cannot be read as one.
 *
 * The bill keeps the phone AS TYPED at the till (trimmed), so it may be local
 * (`01012345678`), international (`+20 101 234 5678`, `0020…`, also with the
 * trunk 0 kept after the code: `+20 0101…`, `002001…`) or written in
 * Arabic-Indic digits. A local number is read as Egyptian — the stores are
 * Egyptian today; any other country must be typed with its code. A number
 * that fits none of these gives null and the till offers "copy" only, rather
 * than a WhatsApp chat with a stranger.
 */
export function customerWhatsappDigits(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const folded = foldArabicDigits(phone).trim();
  const international = folded.startsWith("+");
  const raw = folded.replace(/\D/g, "");
  const withCode = international || raw.startsWith("00");
  let digits = !international && raw.startsWith("00") ? raw.slice(2) : raw;
  if (withCode) {
    // The country code followed by the local trunk 0 (`+20 010…`, `002001…`),
    // common at tills: drop that 0.
    if (/^2001[0125]\d{8}$/.test(digits)) digits = `20${digits.slice(3)}`;
  } else if (/^01[0125]\d{8}$/.test(digits)) digits = `20${digits.slice(1)}`;
  else if (/^1[0125]\d{8}$/.test(digits)) digits = `20${digits}`;
  else if (/^2001[0125]\d{8}$/.test(digits)) digits = `20${digits.slice(3)}`;
  else if (!/^201[0125]\d{8}$/.test(digits)) return null;
  return /^[1-9]\d{7,14}$/.test(digits) ? digits : null;
}

/** `https://wa.me/<digits>?text=<message>`. */
export function trackingWhatsappUrl(digits: string, message: string): string {
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}
